import React from 'react';
import { Typography, Button, Flex } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useTrackBillingEvent, useSuggestedPlan } from '~features/billing';
import { useTrial } from '../useTrial';

const WidgetHeights = {
  TRIAL_STARTED: 64,
} as const;

type TrialStartedWidgetProps = {
  daysLeft: number;
  onUpgrade: () => void;
};

function TrialStartedWidget({ daysLeft, onUpgrade }: TrialStartedWidgetProps) {
  return (
    <Flex justify="space-between" align="center">
      {daysLeft > 1 ? (
        <Typography.Text style={{ fontSize: 12 }} strong>
          {Math.ceil(daysLeft)} days left in trial
        </Typography.Text>
      ) : (
        <Typography.Text style={{ fontSize: 12 }} strong type="danger">
          1 day left in trial
        </Typography.Text>
      )}

      <Button variant="outlined" onClick={onUpgrade}>
        Upgrade
      </Button>
    </Flex>
  );
}

type SidebarWidgetProps = {
  isCollapsed: boolean;
  height: number;
};

function SidebarWidget({ isCollapsed, height }: SidebarWidgetProps) {
  const { isInTrial, daysLeftOnTrial } = useTrial();
  const trackBillingEvent = useTrackBillingEvent();
  const suggestedPlan = useSuggestedPlan();

  const navigate = useNavigate();

  const navigateToUpgrade = () => {
    trackBillingEvent('UpgradeClicked', { cta_location: 'Free Trial Sidebar', cta_name: 'Upgrade' });
    if (suggestedPlan) navigate(`/billing/plans?selectedPlan=${suggestedPlan.id}`);
    else navigate(`/billing/plans`);
  };

  if (isCollapsed || !isInTrial) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#FAFAFA',
        height,
        width: 280,
        borderTop: `1px solid #F0F0F0`,
        padding: 16,
      }}
    >
      {isInTrial && !!daysLeftOnTrial && (
        <TrialStartedWidget daysLeft={daysLeftOnTrial} onUpgrade={navigateToUpgrade} />
      )}
    </div>
  );
}

export function useSidebarWidgetHeight() {
  const { isInTrial } = useTrial();

  if (isInTrial) return WidgetHeights.TRIAL_STARTED;
  return 0;
}

export default SidebarWidget;
